import API from "../../../api/index";
import { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { boxStyle } from "../../../theme";
import { TiresiaBrain } from "../../../components/GeneralIcons";
import { Stack, Skeleton } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const getColor = (value) => (value === 100 ? "#4caf50" : "#1976d2");

const barSx = (color) => ({
  flex: 1,
  height: 10,
  borderRadius: 5,
  backgroundColor: "rgba(255, 255, 255, 0.08)",
  "& .MuiLinearProgress-bar": { backgroundColor: color },
});

// Orion's own risk/CVE data needs a stretch of accumulated telemetry before it's
// trustworthy (traffic baselines, event history) — the same reasoning Tiresia's
// bar already existed for, just a fixed window instead of a live "how full is
// the buffer" read. Day 15 is a product-level judgment call: enough days for a
// weekly pattern (workdays vs weekend) to show up twice.
//
// Counts from `orionFirstSeen` (earliest asset actually reported BY Orion for
// this LECS), not from the licence's `activatedAt` — a licence can be toggled
// on well before Orion sends any data, so activation time alone doesn't say
// when Orion's own risk data actually started accumulating.
const ORION_ONBOARDING_DAYS = 15;

const orionOnboardingProgress = (orionFirstSeen) => {
  if (!orionFirstSeen) return null;
  const startMs = new Date(orionFirstSeen).getTime();
  if (Number.isNaN(startMs)) return null;
  const totalMs = ORION_ONBOARDING_DAYS * 24 * 60 * 60 * 1000;
  const elapsedMs = Date.now() - startMs;
  const value = Math.max(0, Math.min(100, Math.round((elapsedMs / totalMs) * 100)));
  const daysElapsed = Math.max(0, Math.min(ORION_ONBOARDING_DAYS, Math.floor(elapsedMs / (24 * 60 * 60 * 1000))));
  return { value, daysElapsed };
};

// One row inside the card: a labeled progress bar, nothing else — the caption
// explaining what the number means lives only in the detail popup (opened by
// clicking the card), not repeated under every bar here.
// Shared by every progress item the card tracks — Tiresia's live buffer read today,
// Orion's fixed onboarding window, and whatever else lands here later.
const ProgressRow = ({ icon: Icon, label, value }) => {
  const color = getColor(value);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.8} mb={0.6}>
        <Icon sx={{ fontSize: 15, color: "#888" }} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#aaa" }}>{label}</Typography>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" width="100%">
        <LinearProgress variant="determinate" value={value} sx={barSx(color)} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color, minWidth: 36, textAlign: "right" }}>
          {value}%
        </Typography>
      </Stack>
    </Box>
  );
};

// Generic "how ready is this platform capability" card — started as a single
// Tiresia-only progress bar, generalized to hold one row per capability that has
// a ramp-up period before its output can be trusted. Rows that don't currently
// apply (Orion not yet activated) are simply omitted rather than shown at 0%,
// since "not activated" and "just activated" are different states worth telling
// apart.
const TiresiaLoad = () => {
  const [buffer, setBuffer] = useState(0);
  const [displayedBuffer, setDisplayedBuffer] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orionOnboarding, setOrionOnboarding] = useState(null);

  useEffect(() => {
    if (buffer === 100) {
      let current = displayedBuffer;
      const interval = setInterval(() => {
        current += 2;
        if (current >= 100) {
          current = 100;
          clearInterval(interval);
        }
        setDisplayedBuffer(current);
      }, 25);
      return () => clearInterval(interval);
    } else {
      setDisplayedBuffer(buffer);
    }
  }, [buffer, displayedBuffer]);

  useEffect(() => {
    const controller = new AbortController()
    API.getTiresiaLevel(controller.signal).then((res) => {
      const level = Math.round(res.data.level * 100) / 100;
      setBuffer(level);
    }).catch((err) => {
      if (err.name !== 'CanceledError') {}
    }).finally(() => setLoading(false));
    return () => controller.abort()
  }, []);

  useEffect(() => {
    // getOrionVisibility doesn't take an abort signal (see OrionContext.jsx's
    // own call) — nothing to cancel here.
    API.getOrionVisibility()
      .then((res) => setOrionOnboarding(orionOnboardingProgress(res?.data?.orionFirstSeen)))
      .catch(() => setOrionOnboarding(null));
  }, []);

  const color = getColor(buffer);

  return (
    <>
      <Box
        className="tiresiaLoad"
        sx={{ ...boxStyle, cursor: "pointer", display: "flex", flexDirection: "column" }}
        onClick={() => setOpenDialog(true)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#888" }}>
            Platform Readiness
          </Typography>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PsychologyIcon sx={{ fontSize: 18, color: "#888" }} />
          </Box>
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center" width="100%">
            <Skeleton variant="rounded" height={10} sx={{ flex: 1, bgcolor: "rgba(255,255,255,0.08)" }} />
            <Skeleton variant="rounded" width={40} height={20} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
          </Stack>
        ) : (
          <Stack spacing={1.4}>
            <ProgressRow icon={PsychologyIcon} label="Tiresia Training" value={buffer} />
            {orionOnboarding && (
              <ProgressRow icon={AutoAwesomeIcon} label="Orion Onboarding" value={orionOnboarding.value} />
            )}
          </Stack>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TiresiaBrain sx={{ color }} />
          Platform Readiness Detail
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Tiresia Training
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <LinearProgress variant="determinate" value={buffer} sx={barSx(color)} />
                <Typography fontWeight="bold" sx={{ color }}>{buffer}%</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {buffer < 100 ? "Tiresia is still learning from network data." : "Tiresia has completed training."}
              </Typography>
            </Box>

            {orionOnboarding && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Orion Onboarding
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <LinearProgress variant="determinate" value={orionOnboarding.value} sx={barSx(getColor(orionOnboarding.value))} />
                    <Typography fontWeight="bold" sx={{ color: getColor(orionOnboarding.value) }}>{orionOnboarding.value}%</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                                        {orionOnboarding.value < 100
                      ? `Orion needs to capture more data before its risk scoring should be considered reliable.`
                      : `Orion's risk data is now backed by a reliable baseline.`}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} sx={{ color }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TiresiaLoad;
